import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
    VStack,
    TableContainer,
    Spinner,
    useColorMode,
} from '@chakra-ui/react';
import {
    AiOutlineTeam,
    AiOutlinePlus,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineSearch,
    AiOutlineLock,
    AiOutlineUser,
} from 'react-icons/ai';
import api from '../../../services/api';
import { usePermissions } from '../../../contexts/AuthPermissionsContext';
import ConfirmDeleteDialog from '../../../components/UI/ConfirmDeleteDialog';
import SearchableSelect from '../../../components/UI/SearchableSelect';

const FORM_INICIAL = { nome: '', email: '', is_admin: 0, permissions: 1, password: '' };

// Papel definido pela coluna is_admin: 1 = Admin (vê tudo); senão is_comercial >= 1 = Comercial.
// is_comercial = 2 (esquema legado de admin) ainda é reconhecido para compatibilidade.
const roleDe = (u) =>
    (Number(u?.is_admin) === 1 || Number(u?.is_comercial) >= 2) ? 'admin'
    : Number(u?.is_comercial) >= 1 ? 'comercial'
    : 'none';

const ROLES_MAP = {
    admin: { label: 'Admin', cor: '#01E975', desc: 'Acesso total de administrador' },
    comercial: { label: 'Comercial', cor: '#4299E1', desc: 'Acesso à visão comercial (dropdown comercial, calendário e consulta marca)' },
    none: { label: 'Sem acesso', cor: '#718096', desc: 'Sem acesso ao Comercial (pendente de liberação)' },
};

const Usuarios = () => {
    const { colorMode } = useColorMode();
    const toast = useToast();
    const { employees, refetchEmployees } = usePermissions();
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modals
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

    const [busca, setBusca] = useState('');
    const [editando, setEditando] = useState(null);
    const [usuarioVisualizando, setUsuarioVisualizando] = useState(null);
    const [usuarioExcluindo, setUsuarioExcluindo] = useState(null);
    const [form, setForm] = useState(FORM_INICIAL);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    // Autocomplete states
    const [emailFocado, setEmailFocado] = useState(false);

    const fetchUsuarios = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/comercial/usuarios');
            setUsuarios(res.data || []);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
            toast({ title: 'Erro ao carregar usuários', status: 'error', duration: 3000 });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchUsuarios();
    }, [fetchUsuarios]);

    const filtrados = useMemo(() => {
        if (!busca.trim()) return usuarios;
        const q = busca.toLowerCase();
        return usuarios.filter(u =>
            (u.nome || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        );
    }, [usuarios, busca]);

    const totalPages = Math.ceil(filtrados.length / itemsPerPage);
    const paginatedUsuarios = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filtrados.slice(start, start + itemsPerPage);
    }, [filtrados, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [busca]);

    const employeesSugeridos = useMemo(() => {
        const query = form.email?.trim().toLowerCase();
        if (!query) return [];
        return employees.filter(e => 
            (e.email || '').toLowerCase().includes(query) || 
            (e.name || '').toLowerCase().includes(query)
        ).slice(0, 8);
    }, [employees, form.email]);

    const handleAbrir = useCallback((user = null) => {
        setEditando(user);
        if (user) {
            setForm({
                nome: user.nome || '',
                email: user.email || '',
                is_admin: roleDe(user) === 'admin' ? 1 : 0,
                permissions: Number(user.permissions ?? 1) >= 1 ? 1 : 0,
                password: '',
            });
        } else {
            setForm(FORM_INICIAL);
        }
        onOpen();
    }, [onOpen]);

    const handleFechar = useCallback(() => {
        setEditando(null);
        setForm(FORM_INICIAL);
        onClose();
    }, [onClose]);

    const handleVisualizar = useCallback((user) => {
        setUsuarioVisualizando(user);
        onViewOpen();
    }, [onViewOpen]);

    const handleFecharVisualizar = useCallback(() => {
        setUsuarioVisualizando(null);
        onViewClose();
    }, [onViewClose]);

    const handleSalvar = useCallback(async () => {
        if (!form.email.trim()) {
            toast({ title: 'O e-mail é obrigatório', status: 'warning', duration: 2000 });
            return;
        }

        const payload = {
            email: form.email.trim(),
            is_comercial: 1, // acesso comercial (o papel de admin vive em is_admin)
            is_admin: Number(form.is_admin) === 1 ? 1 : 0,
            permissions: Number(form.permissions) >= 1 ? 1 : 0,
            password: form.password.trim() || undefined,
        };

        try {
            if (editando) {
                await api.put(`/comercial/usuarios/${editando.id}`, payload);
                toast({ title: 'Usuário atualizado com sucesso', status: 'success', duration: 2000 });
            } else {
                if (!form.password.trim()) {
                    toast({ title: 'Senha obrigatória para novos usuários', status: 'warning', duration: 2000 });
                    return;
                }
                await api.post('/comercial/usuarios', payload);
                toast({ title: 'Usuário criado com sucesso', status: 'success', duration: 2000 });
            }
            fetchUsuarios();
            refetchEmployees(); // Atualizar context para refletir mudanças locais imediatas
            handleFechar();
        } catch (err) {
            console.error('Erro ao salvar usuário:', err);
            toast({
                title: 'Erro ao salvar',
                description: err.response?.data?.message || 'Falha ao conectar com o banco de dados.',
                status: 'error',
                duration: 4000
            });
        }
    }, [form, editando, fetchUsuarios, refetchEmployees, handleFechar, toast]);

    const handleExcluir = useCallback((user) => {
        setUsuarioExcluindo(user);
        onDelOpen();
    }, [onDelOpen]);

    const handleConfirmarExcluir = useCallback(async () => {
        if (!usuarioExcluindo) return;
        try {
            await api.delete(`/comercial/usuarios/${usuarioExcluindo.id}`);
            toast({ title: 'Usuário removido com sucesso', status: 'info', duration: 2000 });
            fetchUsuarios();
            refetchEmployees();
            onDelClose();
            setUsuarioExcluindo(null);
        } catch (err) {
            console.error('Erro ao excluir usuário:', err);
            toast({ title: 'Erro ao excluir usuário', status: 'error', duration: 3000 });
        }
    }, [usuarioExcluindo, fetchUsuarios, refetchEmployees, onDelClose, toast]);

    const getInitials = (nome) => {
        if (!nome) return '?';
        return nome.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
    };

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="100%">
                <Box flexShrink={0}>
                    <Heading size="lg" color="textPrimary" fontWeight="900">
                        <HStack spacing={3}>
                            <Icon as={AiOutlineTeam} color="secondary" />
                            <Text>Usuários</Text>
                        </HStack>
                    </Heading>
                </Box>
                <HStack spacing={3} flexWrap="wrap" justify="flex-end" flexShrink={1} minW={0}>
                    <InputGroup maxW={{ base: '100%', md: '260px' }} minW="160px" flex="1 1 200px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="textSecondary" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar usuário..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            name="busca-usuario-nogle"
                            autoComplete="off"
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                            color="textPrimary"
                            borderRadius="xl"
                        />
                    </InputGroup>
                    <Button
                        leftIcon={<AiOutlinePlus />}
                        size="sm"
                        bg="secondary"
                        color="primary"
                        _hover={{ bg: 'secondaryHover' }}
                        fontWeight="800"
                        onClick={() => handleAbrir()}
                        flexShrink={0}
                    >
                        Novo Usuário
                    </Button>
                </HStack>
            </Flex>

            {/* Tabela */}
            <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                <Flex
                    align="center"
                    gap={3}
                    p={4}
                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                    borderBottom="1px solid"
                    borderColor="borderPrimary"
                >
                    <Icon as={AiOutlineLock} color="secondary" boxSize="20px" />
                    <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1.5px" fontWeight="800">
                        Gerenciamento de Acesso
                    </Heading>
                    {loading ? (
                        <Spinner size="xs" color="secondary" ml="auto" />
                    ) : (
                        <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">
                            {filtrados.length}
                        </Badge>
                    )}
                </Flex>
                <TableContainer>
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Nome (Colaborador)</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">E-mail</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Permissão</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Status</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {loading ? (
                                <Tr>
                                    <Td colSpan={5} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                                        <Spinner size="md" color="secondary" mr={2} /> Carregando usuários comerciais...
                                    </Td>
                                </Tr>
                            ) : filtrados.length === 0 ? (
                                <Tr>
                                    <Td colSpan={5} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                                        Nenhum usuário comercial cadastrado
                                    </Td>
                                </Tr>
                            ) : (
                                paginatedUsuarios.map(user => {
                                    const roleInfo = ROLES_MAP[roleDe(user)];
                                    const ativo = Number(user.permissions) >= 1;
                                    return (
                                        <Tr key={user.id} _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }} cursor="pointer" onClick={() => handleVisualizar(user)}>
                                            <Td borderColor="borderPrimary">
                                                <HStack spacing={3}>
                                                    <Avatar
                                                        size="sm"
                                                        name={user.nome || user.email}
                                                        bg={`${roleInfo.cor}25`}
                                                        color={roleInfo.cor}
                                                        fontWeight="900"
                                                        fontSize="xs"
                                                    >
                                                        {getInitials(user.nome || user.email)}
                                                    </Avatar>
                                                    <Text fontWeight="800" fontSize="sm" color="textPrimary">
                                                        {user.nome || '—'}
                                                    </Text>
                                                </HStack>
                                            </Td>
                                            <Td borderColor="borderPrimary">
                                                <Text fontSize="sm" color="textSecondary">{user.email}</Text>
                                            </Td>
                                            <Td borderColor="borderPrimary">
                                                <Badge
                                                    bg={`${roleInfo.cor}20`}
                                                    color={roleInfo.cor}
                                                    fontSize="xs"
                                                    fontWeight="700"
                                                    borderRadius="md"
                                                    px={2}
                                                >
                                                    {roleInfo.label}
                                                </Badge>
                                            </Td>
                                            <Td borderColor="borderPrimary">
                                                <Badge
                                                    bg={ativo ? 'rgba(1,233,117,0.15)' : 'rgba(229,62,62,0.12)'}
                                                    color={ativo ? '#01E975' : '#FC8181'}
                                                    fontSize="10px"
                                                    fontWeight="700"
                                                    borderRadius="md"
                                                    px={2}
                                                >
                                                    {ativo ? 'Ativo' : 'Inativo'}
                                                </Badge>
                                            </Td>
                                            <Td borderColor="borderPrimary" isNumeric onClick={e => e.stopPropagation()}>
                                                <HStack spacing={1} justify="flex-end">
                                                    <IconButton
                                                        aria-label="Editar"
                                                        icon={<AiOutlineEdit />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="textSecondary"
                                                        _hover={{ color: 'secondary', bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }}
                                                        onClick={() => handleAbrir(user)}
                                                    />
                                                    <IconButton
                                                        aria-label="Excluir"
                                                        icon={<AiOutlineDelete />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="textSecondary"
                                                        _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                                                        onClick={() => handleExcluir(user)}
                                                    />
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                    <HStack justify="center" spacing={2} py={4} borderTop="1px solid" borderColor="borderPrimary" bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}>
                        <Button
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                            isDisabled={currentPage === 1}
                            variant="outline"
                            borderColor="borderPrimary"
                            color="textSecondary"
                            _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'textPrimary' }}
                        >
                            Anterior
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                            <Button
                                key={pageNumber}
                                size="sm"
                                onClick={() => setCurrentPage(pageNumber)}
                                bg={currentPage === pageNumber ? 'secondary' : 'transparent'}
                                color={currentPage === pageNumber ? 'primary' : 'textSecondary'}
                                border="1px solid"
                                borderColor={currentPage === pageNumber ? 'secondary' : 'borderPrimary'}
                                _hover={{ bg: currentPage === pageNumber ? 'secondaryHover' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100'), color: currentPage === pageNumber ? 'primary' : 'textPrimary' }}
                                fontWeight="800"
                            >
                                {pageNumber}
                            </Button>
                        ))}
                        <Button
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                            isDisabled={currentPage === totalPages}
                            variant="outline"
                            borderColor="borderPrimary"
                            color="textSecondary"
                            _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'textPrimary' }}
                        >
                            Próxima
                        </Button>
                    </HStack>
                )}
            </Box>

            {/* Modal Visualizar Usuário */}
            <Modal isOpen={isViewOpen} onClose={handleFecharVisualizar} isCentered size="lg">
                <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody pt={8} pb={4} px={6}>
                        {usuarioVisualizando && (() => {
                            const roleInfo = ROLES_MAP[roleDe(usuarioVisualizando)];
                            return (
                                <VStack spacing={5} align="stretch">
                                    <Flex direction="column" align="center" gap={3}>
                                        <Avatar
                                            size="xl"
                                            name={usuarioVisualizando.nome || usuarioVisualizando.email}
                                            bg={`${roleInfo.cor}25`}
                                            color={roleInfo.cor}
                                            fontWeight="900"
                                            fontSize="2xl"
                                        >
                                            {getInitials(usuarioVisualizando.nome || usuarioVisualizando.email)}
                                        </Avatar>
                                        <VStack spacing={1}>
                                            <Text fontSize="xl" fontWeight="900" color="textPrimary" textAlign="center">
                                                {usuarioVisualizando.nome || '—'}
                                            </Text>
                                            <Badge
                                                bg={`${roleInfo.cor}20`}
                                                color={roleInfo.cor}
                                                fontSize="xs"
                                                fontWeight="700"
                                                borderRadius="md"
                                                px={2}
                                            >
                                                {roleInfo.label}
                                            </Badge>
                                        </VStack>
                                    </Flex>

                                    <Divider borderColor="borderPrimary" />

                                    <SimpleGrid columns={1} spacing={3}>
                                        <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} p={3} borderRadius="lg" border="1px solid" borderColor="borderPrimary">
                                            <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={1}>
                                                E-mail comercial
                                            </Text>
                                            <Text fontSize="sm" color="textPrimary" fontWeight="700">
                                                {usuarioVisualizando.email || '—'}
                                            </Text>
                                        </Box>
                                        <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} p={3} borderRadius="lg" border="1px solid" borderColor="borderPrimary">
                                            <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={1}>
                                                Descrição de privilégios
                                            </Text>
                                            <Text fontSize="sm" color="textPrimary" fontWeight="700">
                                                {roleInfo.desc}
                                            </Text>
                                        </Box>
                                    </SimpleGrid>
                                </VStack>
                            );
                        })()}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={handleFecharVisualizar} size="sm">
                            Fechar
                        </Button>
                        <Button
                            leftIcon={<AiOutlineEdit />}
                            bg="secondary"
                            color="primary"
                            _hover={{ bg: 'secondaryHover' }}
                            fontWeight="800"
                            onClick={() => {
                                handleFecharVisualizar();
                                handleAbrir(usuarioVisualizando);
                            }}
                            size="sm"
                        >
                            Editar Usuário
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal Criar/Editar */}
            <Modal isOpen={isOpen} onClose={handleFechar} isCentered size="lg">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary">
                        <HStack spacing={3}>
                            <Icon as={AiOutlineUser} color="secondary" boxSize="20px" />
                            <Text>{editando ? 'Editar Usuário' : 'Novo Usuário'}</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            {/* Autocomplete E-mail google */}
                            <FormControl position="relative" isRequired={!editando} isDisabled={!!editando}>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    E-mail
                                </FormLabel>
                                <Input
                                    value={form.email}
                                    onChange={e => setForm(p => ({ ...p, email: e.target.value, nome: '' }))}
                                    onFocus={() => setEmailFocado(true)}
                                    onBlur={() => setTimeout(() => setEmailFocado(false), 250)}
                                    placeholder="email@conteltelecom.com.br"
                                    type="email"
                                    autoComplete="off"
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    color="textPrimary"
                                    _placeholder={{ color: 'textSecondary' }}
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                                {emailFocado && employeesSugeridos.length > 0 && (
                                    <Box
                                        position="absolute"
                                        top="100%"
                                        left={0}
                                        right={0}
                                        bg="surface"
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        borderRadius="md"
                                        zIndex={2000}
                                        maxH="200px"
                                        overflowY="auto"
                                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                                    >
                                        {employeesSugeridos.map(emp => (
                                            <Box
                                                key={emp.id}
                                                px={3}
                                                py={2}
                                                cursor="pointer"
                                                _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'secondary' }}
                                                onMouseDown={() => {
                                                    setForm(p => ({
                                                        ...p,
                                                        email: emp.email,
                                                        nome: emp.name
                                                    }));
                                                    setEmailFocado(false);
                                                }}
                                                fontSize="sm"
                                                color="textPrimary"
                                            >
                                                <Text fontWeight="800" fontSize="xs">{emp.name}</Text>
                                                <Text fontSize="10px" color="textSecondary">{emp.email}</Text>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </FormControl>

                            <FormControl isDisabled>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Nome do Colaborador (Auto-preenchido)
                                </FormLabel>
                                <Input
                                    value={form.nome}
                                    placeholder="Nome completo (selecione o e-mail acima)"
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    color="textPrimary"
                                />
                            </FormControl>

                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Permissão
                                    </FormLabel>
                                    <SearchableSelect
                                        value={form.is_admin}
                                        onChange={v => v != null && setForm(p => ({ ...p, is_admin: Number(v) }))}
                                        options={[
                                            { value: 0, label: 'Comercial — Visão comercial' },
                                            { value: 1, label: 'Admin — Acesso total' },
                                        ]}
                                        placeholder="Buscar permissão..."
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Status
                                    </FormLabel>
                                    <SearchableSelect
                                        value={form.permissions}
                                        onChange={v => v != null && setForm(p => ({ ...p, permissions: Number(v) }))}
                                        options={[
                                            { value: 1, label: 'Ativo' },
                                            { value: 0, label: 'Inativo' },
                                        ]}
                                        placeholder="Buscar status..."
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                                <FormControl isRequired={!editando}>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Senha {editando && '(opcional)'}
                                    </FormLabel>
                                    <Input
                                        value={form.password}
                                        onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                        placeholder={editando ? 'Manter senha atual' : 'Defina a senha de acesso'}
                                        type="password"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _placeholder={{ color: 'textSecondary' }}
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                            </HStack>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={handleFechar} size="sm">
                            Cancelar
                        </Button>
                        <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvar} size="sm">
                            {editando ? 'Salvar' : 'Criar Usuário'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirmação padrão de exclusão */}
            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={onDelClose}
                onConfirm={handleConfirmarExcluir}
                title="Excluir Usuário Comercial"
                itemName={usuarioExcluindo?.nome || usuarioExcluindo?.email}
                description="O acesso comercial deste usuário será removido."
            />
        </VStack>
    );
};

export default Usuarios;
